import type { SegmentReveal as Reveal } from '../lib/useGame.js';
import { MINIGAME_META } from './registry.js';
import { color } from '../theme.js';
import { EmoteRow } from './Reactions.js';

/**
 * The beat between two segments.
 *
 * Both players advance together, so when a segment closes there is a moment
 * where the only interesting fact is: did each of us land it? This shows that,
 * then gets out of the way. The server opens the next segment on its own timer;
 * this component never drives the race, it only narrates it.
 */
export function SegmentRevealOverlay({ reveal, onReact }: { reveal: Reveal; onReact: (reaction: string) => void }) {
  const meta = MINIGAME_META[reveal.minigameId];

  const headline = reveal.youSolved
    ? reveal.youWereFirst === true
      ? 'FIRST!'
      : 'SOLVED'
    : 'MISSED';
  const tint = reveal.youSolved ? color.lime : color.redBright;

  return (
    <div
      style={{
        position: 'absolute',
        inset: 0,
        zIndex: 12,
        display: 'grid',
        placeItems: 'center',
        background: 'rgba(4,7,4,.82)',
        backdropFilter: 'blur(2px)',
        animation: 'rhBannerIn .22s ease-out',
      }}
    >
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 14 }}>
        <span style={{ fontSize: 11, letterSpacing: '.2em', color: 'rgba(234,255,234,.45)' }}>
          {meta.title}
        </span>

        <span
          style={{
            fontWeight: 800,
            fontSize: 'clamp(34px,8cqmin,68px)',
            letterSpacing: '.05em',
            lineHeight: 1,
            color: tint,
            textShadow: `0 0 clamp(10px,3cqmin,30px) ${tint}77`,
          }}
        >
          {headline}
        </span>

        <div style={{ display: 'flex', gap: 26, marginTop: 4 }}>
          <Outcome label="YOU" solved={reveal.youSolved} />
          <Outcome label="OPPONENT" solved={reveal.opponentSolved} />
        </div>

        {!reveal.raceComplete && (
          <>
            <div style={{ marginTop: 10 }}>
              <EmoteRow onSend={onReact} />
            </div>
            <span style={{ marginTop: 6, fontSize: 11, letterSpacing: '.14em', color: 'rgba(234,255,234,.35)' }}>
              NEXT GAME…
            </span>
          </>
        )}
      </div>
    </div>
  );
}

function Outcome({ label, solved }: { label: string; solved: boolean }) {
  const tint = solved ? color.lime : 'rgba(255,46,77,.85)';
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 5 }}>
      <span style={{ fontSize: 10, letterSpacing: '.16em', color: 'rgba(234,255,234,.4)' }}>{label}</span>
      <span style={{ fontSize: 22, fontWeight: 800, color: tint }}>{solved ? '✓' : '✗'}</span>
    </div>
  );
}
